// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { pick } from "lodash";
import { flowResult, makeAutoObservable, reaction, runInAction } from "mobx";
import moment from "moment";

import {
  compareSeverityRanks,
  getChargeSeverityRank,
  getMostSevereCharges,
  MostSevereCharge,
} from "~@sentencing/trpc-types";
import {
  Hydratable,
  HydratesFromSource,
  HydrationState,
} from "~hydration-utils";

import { SAR, SARInsight } from "../api";
import { GenderToDisplayName } from "../components/CaseDetails/constants";
import { MutableSARAttributes } from "../components/CaseDetails/types";
import {
  EditableChargeField,
  REQUIRED_FIELD_IDS,
} from "../components/CaseInformation/constants";
import { JudgeOption } from "../components/CaseInformation/JudgeSelector";
import {
  NeedsToBeAddressed,
  OTHER_OPTION,
  ProtectiveFactors,
} from "../components/constants";
import { KEY_CONSIDERATIONS_REQUIRED_FIELDS } from "../components/KeyConsiderations/constants";
import { mapEnumKeysToDisplay } from "../components/KeyConsiderations/utils";
import {
  AssessmentTypeKey,
  Boundaries,
  deriveDomainRiskLevel,
  getAssessmentRiskLevel,
} from "../components/OffenderAssessment/assessmentTypeUtils";
import { RiskLevelKey } from "../components/OffenderAssessment/constants";
import { EmploymentHistory } from "../components/OffenderAssessment/EmploymentHistory/constants";
import {
  DomainConfig,
  getDomainsForAssessmentType,
  getScoreWithAgePoint,
  ORAS_EMPTY_FORM,
  ORASDomainRiskLevelField,
  ORASFormData,
} from "../components/OffenderAssessment/utils";
import {
  DISPOSITION_TYPE_ORDER,
  SARSection,
  type SARSectionName,
} from "../components/SARDetails/constants";
import { SectionStatus } from "../components/SARDetails/StatusIndicator";
import {
  getSARSections,
  isPSRAllExceptVictimImpact,
  isPSRVictimImpactOnly,
  shouldShowInSARReport,
  shouldShowInSARSummary,
} from "../components/SARDetails/utils";
import { InsightDescriptionContext } from "../components/Summary/insightsUtils";
import { RiskProfileCardData } from "../components/Summary/ReportRiskProfileSummaryCard";
import { SignatureData } from "../components/Summary/ReportSignature";
import { SentencingStore } from "../datastores/SentencingStore";
import { FormCharge } from "../datastores/types";
import {
  formatClientFullName,
  formatDisplayDate,
  formatJudgeName,
  formatLongDate,
  formatPersonName,
  titleCase,
} from "../utils/utils";
import { CRIMINAL_HISTORY_DEFAULT, DOMAIN_TO_SUMMARY_FIELD } from "./constants";
import { OffenderAssessmentPresenter } from "./OffenderAssessmentPresenter";
import { PriorTreatmentHistoryPresenter } from "./PriorTreatmentHistoryPresenter";

// Type for SAR metadata structure
type SARMetadataSections = {
  keyConsiderations: {
    areasOfNeed: { skipped: boolean };
    mitigatingFactors: { skipped: boolean };
  };
  defendantStatement: {
    skipped: boolean;
    edited?: boolean;
  };
  victimImpactStatement: {
    skipped: boolean;
    edited?: boolean;
  };
  recommendation: {
    skipped: boolean;
    edited?: boolean;
  };
  priorTreatmentHistory?: {
    edited?: boolean;
  };
  offenderAssessment?: {
    edited?: boolean;
  };
};

type SARMetadata = {
  sections: SARMetadataSections;
  version?: "1.0";
};

const RECOMMENDATION_FIELDS = [
  "communityStrategyRecommendation",
  "homePlan",
  "institutionalStrategyRecommendation",
] as const;

const EMPTY_SECTION = { completed: 0, total: 0 };

// All SAR sections that contribute to progress (excludes read-only Summary)
type ProgressSection = Exclude<SARSectionName, SARSection.SUMMARY>;

// Fixed iteration order for ProgressSection, used to filter progress/status
// calculations down to the sections a given officer is actually responsible
// for (see SARSections).
const PROGRESS_SECTIONS: ProgressSection[] = [
  SARSection.CASE_INFORMATION,
  SARSection.KEY_CONSIDERATIONS,
  SARSection.DEFENDANTS_VERSION,
  SARSection.VICTIM_IMPACT,
  SARSection.OFFENDER_ASSESSMENT,
  SARSection.PRIOR_TREATMENT_HISTORY,
  SARSection.RECOMMENDATION,
];

export type SARReportType = SAR["reportType"];
type EditableScalarField =
  | "defendantDeclinedToParticipate"
  | "involvesSexCrime"
  | "static99RCompleted"
  | "static99RScore";

export class SARDetailsPresenter implements Hydratable {
  private hydrator: HydratesFromSource;

  private disposeInsightReaction: () => void;

  SARData?: SAR;

  insight?: SARInsight | null;

  offenderAssessment: OffenderAssessmentPresenter;

  // TODO(OBT-29467): remove once import skips manually-updated SARs —
  // at that point the DB will only contain user-entered records for manually-updated SARs.
  // Sorted most-recent-first by start date so manually-added and MOCIS-imported
  // records are interspersed rather than grouped by source.
  get employmentHistories(): EmploymentHistory[] {
    const histories = this.SARData?.employmentHistories ?? [];
    const showImported =
      this.sentencingStore.activeFeatureVariants.SARImportEmploymentRecords;
    const filtered = showImported
      ? histories
      : histories.filter((h) => !h.importedFromDOC);
    return [...filtered].sort(
      (a, b) =>
        (b.startDate ? new Date(b.startDate).getTime() : 0) -
        (a.startDate ? new Date(a.startDate).getTime() : 0),
    );
  }

  priorTreatmentHistory: PriorTreatmentHistoryPresenter;

  constructor(
    public readonly sentencingStore: SentencingStore,
    public sarId: string,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.offenderAssessment = new OffenderAssessmentPresenter(
      this,
      this.sentencingStore.apiClient,
    );
    this.priorTreatmentHistory = new PriorTreatmentHistoryPresenter(
      this,
      this.sentencingStore.apiClient,
    );
    this.hydrator = new HydratesFromSource({
      expectPopulated: [
        () => {
          if (this.sentencingStore.staffStore.staffInfo === undefined)
            throw new Error("Failed to load staff details");
        },
        () => {
          if (this.SARData === undefined)
            throw new Error("Failed to load SAR details");
        },
      ],
      populate: async () => {
        if (!this.sentencingStore.staffStore.staffInfo) {
          await flowResult(this.sentencingStore.staffStore.loadStaffInfo());
        }
        const data = await flowResult(
          this.sentencingStore.apiClient.getSARDetails(this.sarId),
        );
        this.SARData = data;
      },
    });

    // Loads the insight once the SAR is loaded. The server looks it up from
    // the saved report, so edits that change it reload it after their save
    // instead (see updateMostSevereOffenseName and saveORASData).
    this.disposeInsightReaction = reaction(
      () => this.SARData?.id,
      (id) => {
        if (id) this.loadInsight();
      },
      { fireImmediately: true },
    );
  }

  dispose(): void {
    this.disposeInsightReaction();
  }

  get hydrationState(): HydrationState {
    return this.hydrator.hydrationState;
  }

  async hydrate(): Promise<void> {
    return this.hydrator.hydrate();
  }

  *loadInsight() {
    const sarData = this.SARData;
    if (!sarData) return;

    try {
      this.insight = yield this.sentencingStore.apiClient.getSARInsight(
        sarData.id,
      );
    } catch (e) {
      console.warn(`[SARDetailsPresenter] Failed to load insight: ${e}`);
      this.insight = null;
    }
  }

  get insightData() {
    return this.insight ?? undefined;
  }

  get emptyStateDescriptionContext(): InsightDescriptionContext | null {
    if (this.insight) {
      const { gender, assessmentRiskLevel, offense } = this.insight;
      return {
        gender,
        riskLevel: assessmentRiskLevel,
        offense,
      };
    }
    // Fallback: build context from sarData when no matching insight exists.
    const sarData = this.SARData;
    const gender = sarData?.client?.gender;
    const offense = this.charges[0]?.offense;
    if (!gender || !offense) return null;
    return {
      gender,
      riskLevel: sarData?.assessmentRiskLevel ?? null,
      offense,
    };
  }

  get sortedDispositionData() {
    if (!this.insight?.dispositionData) return [];
    return [...this.insight.dispositionData].sort(
      (a, b) =>
        (DISPOSITION_TYPE_ORDER[a.recommendationType ?? ""] ?? 4) -
        (DISPOSITION_TYPE_ORDER[b.recommendationType ?? ""] ?? 4),
    );
  }

  get staffPseudoId(): string | undefined {
    return this.sentencingStore.staffPseudoId;
  }

  trackSARCaseDetailsPageViewed(): void {
    this.sentencingStore.analyticsStore.trackSARCaseDetailsPageViewed({
      viewedBy: this.staffPseudoId,
      caseId: this.sarId,
    });
  }

  trackSARDownloadReportClicked(): void {
    this.sentencingStore.analyticsStore.trackSARDownloadReportClicked({
      viewedBy: this.staffPseudoId,
      caseId: this.sarId,
    });
  }

  get SARAttributes() {
    if (!this.SARData) return {};

    const formattedClient = this.SARData.client
      ? {
          ...this.SARData.client,
          fullName: formatPersonName(this.SARData.client.fullName),
        }
      : this.SARData.client;

    return {
      client: formattedClient,
      externalId: this.SARData.client?.externalId,
      age: this.SARData.age,
      charges: this.SARData.charges,
      dueDate: this.SARData.dueDate,
    };
  }

  get SARSections(): SARSectionName[] {
    return getSARSections(this);
  }

  /** Whether a section's data should render on the builder's Summary page;
   * see shouldShowInSARSummary. */
  shouldShowInSummary(section: SARSectionName): boolean {
    return shouldShowInSARSummary(section, this);
  }

  /** Whether a section should render in the DOM report; see
   * shouldShowInSARReport. */
  shouldShowInReport(section: SARSectionName): boolean {
    return shouldShowInSARReport(section, this);
  }

  get formattedClientName(): string {
    return formatClientFullName(this.SARData?.client?.fullName);
  }

  /** Formatted birth date for display */
  get formattedBirthDate(): string {
    return formatDisplayDate(this.SARData?.client?.birthDate);
  }

  /** Formatted date requested for display */
  get formattedDateRequested(): string {
    return formatDisplayDate(this.SARData?.dateRequested);
  }

  /** Formatted gender for display */
  get formattedGender(): string | undefined {
    const gender = this.SARData?.client?.gender;
    return gender ? GenderToDisplayName[gender] : undefined;
  }

  get isVictimImpactOnly(): boolean | null {
    return this.SARData?.isVictimImpactOnly ?? null;
  }

  get reportType(): SARReportType | undefined {
    return this.SARData?.reportType;
  }

  get isPSRBuilderActive(): boolean {
    return Boolean(this.sentencingStore.activeFeatureVariants.PSRBuilder);
  }

  /**
   * True while the officer is revisiting an already-saved report type
   * selection (via the "Change" link), as opposed to never having chosen
   * one. Kept separate from isVictimImpactOnly so that reopening the choice
   * doesn't clobber the saved value, letting "Back" cancel back to it.
   */
  isChangingReportType = false;

  /**
   * Whether the report type selection card should be shown instead of the
   * normal SAR sections: either no selection has been saved yet, or the
   * officer is actively revisiting a saved one. Never shown on a completed
   * report.
   */
  get showReportTypeCard(): boolean {
    if (this.isLocked) return false;
    return (
      (this.reportType === "PSR" &&
        this.isVictimImpactOnly === null &&
        this.isPSRBuilderActive) ||
      this.isChangingReportType
    );
  }

  /** Formatted race/ethnicity for display */
  get formattedRaceOrEthnicity(): string {
    const raceArray = this.SARData?.client?.raceOrEthnicity;
    if (!raceArray || raceArray.length === 0) return "Unknown";

    const formattedRaces = raceArray.map((race) =>
      race
        .trim()
        .replace(/_/g, " ")
        .toLowerCase()
        .split(" ")
        .map((word) => titleCase(word))
        .join(" "),
    );

    return formattedRaces.join(", ");
  }

  /** Extract offense names from charges */
  get offenseNames(): string[] {
    if (!this.SARData?.charges || !Array.isArray(this.SARData.charges)) {
      return [];
    }
    return this.SARData.charges.map((charge) => charge.offense).filter(Boolean);
  }

  /** Get charges array sorted by severity (FELONY A first, null classification last) */
  get charges(): FormCharge[] {
    if (!this.SARData?.charges) return [];
    return [...this.SARData.charges].sort((a, b) =>
      compareSeverityRanks(getChargeSeverityRank(a), getChargeSeverityRank(b)),
    );
  }

  /** Most severe charge(s). Length > 1 means a tie requiring user input. */
  get mostSevereCharges(): MostSevereCharge[] {
    return getMostSevereCharges(this.SARData?.charges ?? []);
  }

  get hasTie(): boolean {
    return this.mostSevereCharges.length > 1;
  }

  // TODO(MO-13053): Use the full lock state once SAR locking ships, so a
  // downloaded report is locked and a reopened one is not.
  get isLocked(): boolean {
    return !!this.SARData?.completionDate;
  }

  async updateMostSevereOffenseName(offenseName: string): Promise<void> {
    if (!this.SARData || this.isLocked) return;

    const sarData = this.SARData;
    const offenseChanged = offenseName !== sarData.mostSevereOffenseName;
    runInAction(() => {
      sarData.mostSevereOffenseName = offenseName;
    });

    await this.sentencingStore.apiClient.updateSARDetails(this.SARData.id, {
      mostSevereOffenseName: offenseName,
      status: this.statusForUpdate,
    });

    if (offenseChanged) this.loadInsight();
  }

  /** Get officer (staff) info */
  get officerInfo() {
    const staff = this.SARData?.staff;
    return {
      name: staff?.fullName ? formatPersonName(staff.fullName) : undefined,
      district: staff?.district?.name,
      address: staff?.officeAddress,
      phoneNumber: staff?.officePhoneNumber,
    };
  }

  get SARIsAssignedToCurrentUser(): boolean {
    const currentUserId =
      this.sentencingStore.staffStore.staffInfo?.pseudonymizedId;
    const assignedId = this.SARData?.staff?.pseudonymizedId;

    if (!currentUserId || !assignedId) return false;
    return currentUserId === assignedId;
  }

  get isSupervisor(): boolean {
    return this.sentencingStore.isSupervisor;
  }

  get officerSignatureData(): SignatureData {
    return {
      signature: this.SARData?.officerSignature ?? null,
      title: this.SARData?.officerTitle ?? null,
      lastSignedAt: this.SARData?.officerLastSignedAt ?? null,
    };
  }

  get supervisorSignatureData(): SignatureData {
    return {
      signature: this.SARData?.supervisorSignature ?? null,
      title: this.SARData?.supervisorTitle ?? null,
      lastSignedAt: this.SARData?.supervisorLastSignedAt ?? null,
    };
  }

  async updateReportType(
    reportType: SARReportType,
    isVictimImpactOnly: boolean | null,
  ): Promise<void> {
    const sarData = this.SARData;
    if (!sarData) return;

    const prev = {
      reportType: sarData.reportType,
      isVictimImpactOnly: sarData.isVictimImpactOnly ?? null,
    };

    runInAction(() => {
      this.updateLocalReportType(reportType, isVictimImpactOnly);
    });

    try {
      await this.sentencingStore.apiClient.updateSARDetails(sarData.id, {
        reportType: reportType,
        isVictimImpactOnly: isVictimImpactOnly,
      });
      runInAction(() => {
        this.isChangingReportType = false;
      });
    } catch (e) {
      runInAction(() => {
        this.updateLocalReportType(prev.reportType, prev.isVictimImpactOnly);
      });
      throw e;
    }
  }

  /** Reopen the report type card to revisit an already-saved selection. */
  startReportTypeChange(): void {
    this.isChangingReportType = true;
  }

  /** Cancel revisiting the report type selection, keeping the saved value. */
  cancelReportTypeChange(): void {
    this.isChangingReportType = false;
  }

  async signOfficer(signature: string, title: string): Promise<void> {
    const sarData = this.SARData;
    if (!sarData) return;

    const now = new Date();
    const prev = {
      signature: sarData.officerSignature,
      title: sarData.officerTitle,
      lastSignedAt: sarData.officerLastSignedAt,
    };

    runInAction(() => {
      sarData.officerSignature = signature;
      sarData.officerTitle = title;
      sarData.officerLastSignedAt = now;
    });

    try {
      await this.sentencingStore.apiClient.updateSARDetails(sarData.id, {
        officerSignature: signature,
        officerTitle: title,
        officerLastSignedAt: now,
        status: this.statusForUpdate,
      });
      runInAction(() => {
        this.updateLocalStatus(this.statusForUpdate);
        this.sentencingStore.analyticsStore.trackSARSignatureAdded({
          viewedBy: this.sentencingStore.staffStore.staffInfo?.pseudonymizedId,
          caseId: sarData.id,
          investigationExternalId: sarData.externalId,
          signatureRole: "Officer",
        });
      });
    } catch (e) {
      runInAction(() => {
        sarData.officerSignature = prev.signature;
        sarData.officerTitle = prev.title;
        sarData.officerLastSignedAt = prev.lastSignedAt;
      });
      throw e;
    }
  }

  async signSupervisor(signature: string, title: string): Promise<void> {
    const sarData = this.SARData;
    if (!sarData) return;

    const now = new Date();
    const prev = {
      signature: sarData.supervisorSignature,
      title: sarData.supervisorTitle,
      lastSignedAt: sarData.supervisorLastSignedAt,
    };

    runInAction(() => {
      sarData.supervisorSignature = signature;
      sarData.supervisorTitle = title;
      sarData.supervisorLastSignedAt = now;
    });

    try {
      await this.sentencingStore.apiClient.updateSARDetails(sarData.id, {
        supervisorSignature: signature,
        supervisorTitle: title,
        supervisorLastSignedAt: now,
        status: this.statusForUpdate,
      });
      runInAction(() => {
        this.updateLocalStatus(this.statusForUpdate);
        this.sentencingStore.analyticsStore.trackSARSignatureAdded({
          viewedBy: this.sentencingStore.staffStore.staffInfo?.pseudonymizedId,
          caseId: sarData.id,
          investigationExternalId: sarData.externalId,
          signatureRole: "Supervisor",
        });
      });
    } catch (e) {
      runInAction(() => {
        sarData.supervisorSignature = prev.signature;
        sarData.supervisorTitle = prev.title;
        sarData.supervisorLastSignedAt = prev.lastSignedAt;
      });
      throw e;
    }
  }

  /** Get client attributes for Case Information section */
  get clientAttributes() {
    return this.SARData?.client;
  }

  /** Check if defendant declined to participate */
  get defendantDeclinedToParticipate(): boolean {
    return this.SARData?.defendantDeclinedToParticipate ?? false;
  }

  /**
   * True when a full ORAS assessment was performed and the defendant participated.
   * False when assessmentDate is absent or the defendant declined to participate.
   */
  get hasOrasAssessment(): boolean {
    return (
      !!this.SARData?.assessmentDate && !this.defendantDeclinedToParticipate
    );
  }

  get assessmentAdministeredBy(): string {
    return this.SARData?.assessmentAdministeredBy ?? "Unknown";
  }

  /** Extract unique judge name/division pairs from all imported charges */
  get judgeOptions(): JudgeOption[] {
    const judgeMap = new Map<string, string | null>();
    this.SARData?.charges?.forEach((charge) => {
      (charge.judgeNames ?? []).forEach((name) => {
        if (!judgeMap.has(name)) judgeMap.set(name, charge.division ?? null);
      });
    });
    return Array.from(judgeMap.entries()).map(([name, division]) => ({
      label: formatJudgeName(name),
      value: name,
      division,
    }));
  }

  /**
   * Calculate overall SAR progress at the field level.
   * Each per-section getter counts its own {completed, total} so the
   * progress bar advances as individual fields are filled in.
   * Totals are derived from source arrays (REQUIRED_FIELD_IDS,
   * KEY_CONSIDERATIONS_REQUIRED_FIELDS, RECOMMENDATION_FIELDS, etc.)
   * so adding fields to those arrays automatically updates progress.
   *
   * Returns percentage: 0-100
   */
  get overallProgress(): number {
    const counts = this.sectionFieldCounts;
    if (counts.total === 0) return 0;
    return (counts.completed / counts.total) * 100;
  }

  /** True when this officer is assigned everything other than the Victim Impact section. */
  get isPSRAllExceptVictimImpact(): boolean {
    return isPSRAllExceptVictimImpact(this.isVictimImpactOnly, this.reportType);
  }

  /** True when this officer is assigned only the Victim Impact section. */
  get isPSRVictimImpactOnly(): boolean {
    return isPSRVictimImpactOnly(this.isVictimImpactOnly, this.reportType);
  }

  private get sectionFieldCounts(): { completed: number; total: number } {
    const counts: Record<
      ProgressSection,
      { completed: number; total: number }
    > = {
      [SARSection.CASE_INFORMATION]: this.caseInfoFieldCounts,
      [SARSection.KEY_CONSIDERATIONS]: this.keyConsiderationsFieldCounts,
      [SARSection.DEFENDANTS_VERSION]: this.defendantDeclinedToParticipate
        ? EMPTY_SECTION
        : this.defendantVersionFieldCounts,
      [SARSection.VICTIM_IMPACT]: this.victimImpactFieldCounts,
      [SARSection.OFFENDER_ASSESSMENT]: this.offenderAssessmentFieldCounts,
      [SARSection.PRIOR_TREATMENT_HISTORY]:
        this.priorTreatmentHistoryFieldCounts,
      [SARSection.RECOMMENDATION]: this.recommendationFieldCounts,
    };
    // Only sum sections this officer is actually responsible for.
    const sections = this.SARSections;
    return PROGRESS_SECTIONS.filter((section) => sections.includes(section))
      .map((section) => counts[section])
      .reduce(
        (acc, c) => ({
          completed: acc.completed + c.completed,
          total: acc.total + c.total,
        }),
        EMPTY_SECTION,
      );
  }

  private get caseInfoFieldCounts(): { completed: number; total: number } {
    let completed = 0;
    let total = 0;

    // Judge name is required at the SAR level
    total++;
    if (this.SARData?.requestingJudgeName) completed++;

    // When charges tie on severity, the user must pick the driving offense
    if (this.hasTie) {
      total++;
      if (this.SARData?.mostSevereOffenseName) completed++;
    }

    this.charges.forEach((charge) => {
      REQUIRED_FIELD_IDS.forEach((fieldId) => {
        total++;
        const value = charge[fieldId as keyof typeof charge];
        if (value !== null && value !== undefined && value !== "") {
          completed++;
        }
      });
    });
    return { completed, total };
  }

  private get keyConsiderationsFieldCounts(): {
    completed: number;
    total: number;
  } {
    if (this.defendantDeclinedToParticipate === true) {
      return EMPTY_SECTION;
    }

    let completed = 0;
    const total = KEY_CONSIDERATIONS_REQUIRED_FIELDS.length;

    const hasNeeds =
      this.SARData?.needsToBeAddressed &&
      this.SARData.needsToBeAddressed.length > 0;
    if (hasNeeds || this.needsSkipped) completed++;

    const hasFactors =
      this.SARData?.mitigatingFactors &&
      this.SARData.mitigatingFactors.length > 0;
    if (hasFactors || this.factorsSkipped) completed++;

    return { completed, total };
  }

  private get defendantVersionFieldCounts(): {
    completed: number;
    total: number;
  } {
    const hasContent =
      !!this.SARData?.defendantStatement &&
      this.SARData.defendantStatement.trim() !== "";
    const completed = hasContent || this.defendantStatementSkipped ? 1 : 0;
    return { completed, total: 1 };
  }

  private get victimImpactFieldCounts(): {
    completed: number;
    total: number;
  } {
    // Victim Impact deliberately stays in SARSections for the
    // all-except-victim-impact split (so its nav tab remains visible), so
    // this exclusion can't be derived from SARSections membership the way
    // the other sections' isPSRVictimImpactOnly exclusion is in
    // sectionFieldCounts above.
    if (this.isPSRAllExceptVictimImpact) return EMPTY_SECTION;

    const hasContent =
      !!this.SARData?.victimImpactStatement &&
      this.SARData.victimImpactStatement.trim() !== "";
    const completed = hasContent || this.victimImpactStatementSkipped ? 1 : 0;
    return { completed, total: 1 };
  }

  private get recommendationFieldCounts(): {
    completed: number;
    total: number;
  } {
    if (this.defendantDeclinedToParticipate === true) {
      return EMPTY_SECTION;
    }

    if (this.recommendationSkipped) {
      return {
        completed: RECOMMENDATION_FIELDS.length,
        total: RECOMMENDATION_FIELDS.length,
      };
    }
    let completed = 0;
    RECOMMENDATION_FIELDS.forEach((fieldName) => {
      const value = this.SARData?.[fieldName];
      if (value && value.trim() !== "") completed++;
    });
    return { completed, total: RECOMMENDATION_FIELDS.length };
  }

  private get offenderAssessmentFieldCounts(): {
    completed: number;
    total: number;
  } {
    const { summaries, formFields } = this.offenderAssessmentFields;
    let completed = 0;
    summaries.forEach((s) => {
      if (s && s.trim() !== "") completed++;
    });
    formFields.forEach((f) => {
      if (f !== null && f !== undefined && f.toString().trim() !== "")
        completed++;
    });
    return { completed, total: summaries.length + formFields.length };
  }

  private get priorTreatmentHistoryFieldCounts(): {
    completed: number;
    total: number;
  } {
    if (this.defendantDeclinedToParticipate) return EMPTY_SECTION;
    const summary = this.SARData?.priorTreatmentHistorySummary;
    const hasContent = !!summary && summary.trim() !== "";
    return { completed: hasContent ? 1 : 0, total: 1 };
  }

  /**
   * Status to persist on any update. Complete when all required fields are
   * filled, InProgress otherwise (we never send NotYetStarted — once any
   * edit is made the SAR is always at least InProgress).
   */
  private get statusForUpdate(): "Complete" | "InProgress" {
    return this.overallProgress === 100 ? "Complete" : "InProgress";
  }

  /**
   * Updates the SAR status in both the detail record and the dashboard list.
   * Must be called inside a runInAction block.
   */
  private updateLocalStatus(
    status: "Complete" | "InProgress" | "NotYetStarted",
  ): void {
    if (this.SARData) {
      this.SARData.status = status;
    }
    // Keep the dashboard list in sync so it reflects immediately without a refresh
    const sarList =
      this.sentencingStore.staffStore.staffInfo?.sentencingAssessmentReports;
    if (sarList) {
      const sarInList = sarList.find((sar) => sar.id === this.sarId);
      if (sarInList) {
        sarInList.status = status;
      }
    }
  }

  /**
   * Updates the report type/scope in both the detail record and the
   * dashboard list. Must be called inside a runInAction block.
   */
  private updateLocalReportType(
    reportType: SARReportType,
    isVictimImpactOnly: boolean | null,
  ): void {
    if (this.SARData) {
      this.SARData.reportType = reportType;
      this.SARData.isVictimImpactOnly = isVictimImpactOnly;
    }
    // Keep the dashboard list in sync so it reflects immediately without a refresh
    const sarList =
      this.sentencingStore.staffStore.staffInfo?.sentencingAssessmentReports;
    if (sarList) {
      const sarInList = sarList.find((sar) => sar.id === this.sarId);
      if (sarInList) {
        sarInList.reportType = reportType;
        sarInList.isVictimImpactOnly = isVictimImpactOnly;
      }
    }
  }

  /** Check if a specific charge has all required fields filled */
  isChargeComplete(charge: FormCharge): boolean {
    return REQUIRED_FIELD_IDS.every((fieldId) => {
      const value = charge[fieldId as keyof typeof charge];
      return value !== null && value !== undefined && value !== "";
    });
  }

  /**
   * Generic helper to update a scalar (boolean/number) field in SAR data.
   * Handles local state update, API call, and status recalculation.
   */
  async updateScalarField<K extends EditableScalarField>(
    fieldName: K,
    value: MutableSARAttributes[K],
  ): Promise<void> {
    if (!this.SARData || this.isLocked) return;

    // Update local state immediately
    runInAction(() => {
      if (this.SARData) {
        Object.assign(this.SARData, { [fieldName]: value });
      }
    });

    // Persist to backend with updated status
    const updates: Partial<MutableSARAttributes> = {
      [fieldName]: value,
      status: this.statusForUpdate,
    };
    await this.sentencingStore.apiClient.updateSARDetails(
      this.SARData.id,
      updates,
    );

    // Update local status after successful save
    runInAction(() => {
      this.updateLocalStatus(this.statusForUpdate);
    });
  }

  /** Update defendant declined to participate */
  async updateDefendantDeclined(value: boolean): Promise<void> {
    return this.updateScalarField("defendantDeclinedToParticipate", value);
  }

  /** Update whether this case involves a sex crime */
  async updateInvolvesSexCrime(value: boolean): Promise<void> {
    return this.updateScalarField("involvesSexCrime", value);
  }

  /** Update whether a Static-99R was completed for this case */
  async updateStatic99RCompleted(value: boolean): Promise<void> {
    return this.updateScalarField("static99RCompleted", value);
  }

  /** Update static99RScore */
  async updateStatic99RScore(value: number | null): Promise<void> {
    return this.updateScalarField("static99RScore", value);
  }

  /** Update requesting judge name and optionally division */
  async updateJudgeSelection(
    name: string | null,
    division?: string | null,
  ): Promise<void> {
    if (!this.SARData || this.isLocked) return;

    runInAction(() => {
      if (this.SARData) {
        this.SARData.requestingJudgeName = name;
        if (division !== undefined) this.SARData.division = division;
      }
    });

    const updates: Partial<MutableSARAttributes> = {
      requestingJudgeName: name,
      ...(division !== undefined ? { division } : {}),
      status: this.statusForUpdate,
    };
    await this.sentencingStore.apiClient.updateSARDetails(
      this.SARData.id,
      updates,
    );

    runInAction(() => {
      this.updateLocalStatus(this.statusForUpdate);
    });
  }

  /** Update a charge field */
  async updateChargeField<K extends EditableChargeField>(
    chargeId: string,
    fieldId: K,
    value: string,
  ): Promise<void> {
    if (!this.SARData?.charges || this.isLocked) return;

    // Find the charge in the source data (not the getter)
    const charge = this.SARData.charges.find((c) => c.id === chargeId) as
      | FormCharge
      | undefined;
    if (!charge) return;

    // Store original value for potential rollback
    const originalValue = charge[fieldId];

    // Update local state immediately for instant UI feedback
    runInAction(() => {
      charge[fieldId] = value;
    });

    // Persist to backend with updated status
    try {
      const updates: Partial<MutableSARAttributes> = {
        charges: this.SARData.charges.map((c) => ({
          id: c.id,
          prosecutingAttorney: c.prosecutingAttorney ?? null,
          defenseAttorney: c.defenseAttorney ?? null,
          pleaAgreement: c.pleaAgreement ?? null,
          pleaDate: c.pleaDate ? moment.utc(c.pleaDate).toDate() : null,
          sentencingDate: c.sentencingDate
            ? moment.utc(c.sentencingDate).toDate()
            : null,
        })),
        status: this.statusForUpdate,
      };
      await this.sentencingStore.apiClient.updateSARDetails(
        this.SARData.id,
        updates,
      );

      // Update local status after successful save
      runInAction(() => {
        this.updateLocalStatus(this.statusForUpdate);
      });
    } catch (error) {
      // Revert local change on error
      runInAction(() => {
        charge[fieldId] = originalValue;
      });
      // Re-throw to allow UI to handle (e.g., show error toast)
      throw error;
    }
  }

  /** Update needs to be addressed */
  async updateNeedsToBeAddressed(values: string[]): Promise<void> {
    if (!this.SARData || this.isLocked) return;

    // Update local state immediately
    runInAction(() => {
      if (this.SARData) {
        this.SARData.needsToBeAddressed = values as SAR["needsToBeAddressed"];
      }
    });

    // Persist to backend with updated status
    const updates: Partial<MutableSARAttributes> = {
      needsToBeAddressed: values as SAR["needsToBeAddressed"],
      status: this.statusForUpdate,
    };
    await this.sentencingStore.apiClient.updateSARDetails(
      this.SARData.id,
      updates,
    );

    // Update local status after successful save
    runInAction(() => {
      this.updateLocalStatus(this.statusForUpdate);
    });
  }

  /** Update other need to be addressed */
  async updateOtherNeedToBeAddressed(value: string): Promise<void> {
    if (!this.SARData || this.isLocked) return;

    runInAction(() => {
      if (this.SARData) {
        this.SARData.otherNeedToBeAddressed = value;
      }
    });

    const updates: Partial<MutableSARAttributes> = {
      otherNeedToBeAddressed: value,
      status: this.statusForUpdate,
    };
    await this.sentencingStore.apiClient.updateSARDetails(
      this.SARData.id,
      updates,
    );

    // Update local status after successful save
    runInAction(() => {
      this.updateLocalStatus(this.statusForUpdate);
    });
  }

  /** Update mitigating factors */
  async updateMitigatingFactors(values: string[]): Promise<void> {
    if (!this.SARData || this.isLocked) return;

    runInAction(() => {
      if (this.SARData) {
        this.SARData.mitigatingFactors = values as SAR["mitigatingFactors"];
      }
    });

    const updates: Partial<MutableSARAttributes> = {
      mitigatingFactors: values as SAR["mitigatingFactors"],
      status: this.statusForUpdate,
    };
    await this.sentencingStore.apiClient.updateSARDetails(
      this.SARData.id,
      updates,
    );

    // Update local status after successful save
    runInAction(() => {
      this.updateLocalStatus(this.statusForUpdate);
    });
  }

  /** Update other mitigating factor */
  async updateOtherMitigatingFactor(value: string): Promise<void> {
    if (!this.SARData || this.isLocked) return;

    runInAction(() => {
      if (this.SARData) {
        this.SARData.otherMitigatingFactor = value;
      }
    });

    const updates: Partial<MutableSARAttributes> = {
      otherMitigatingFactor: value,
      status: this.statusForUpdate,
    };
    await this.sentencingStore.apiClient.updateSARDetails(
      this.SARData.id,
      updates,
    );

    // Update local status after successful save
    runInAction(() => {
      this.updateLocalStatus(this.statusForUpdate);
    });
  }

  /**
   * Generic helper to update a string field in SAR data.
   * Handles local state update, API call, and status recalculation.
   */
  async updateStringField(
    fieldName:
      | "victimImpactStatement"
      | "defendantStatement"
      | "communityStrategyRecommendation"
      | "institutionalStrategyRecommendation"
      | "criminalHistorySummary"
      | "employmentSummary"
      | "familyAndSocialSupportSummary"
      | "housingSummary"
      | "homePlan"
      | "drugHistorySummary"
      | "peerAssociatesSummary"
      | "criminalAttitudesSummary"
      | "responsivityAndBarriersSummary"
      | "priorTreatmentHistorySummary"
      | "sexualHistorySummary",
    value: string,
  ): Promise<void> {
    if (!this.SARData || this.isLocked) return;

    runInAction(() => {
      if (this.SARData) {
        this.SARData[fieldName] = value;
      }
    });

    const updates: Partial<MutableSARAttributes> = {
      [fieldName]: value,
      status: this.statusForUpdate,
    };

    // Only include metadata if it exists to avoid validation errors
    if (this.metadata) {
      updates.metadata = this.metadata as SARMetadata;
    }

    await this.sentencingStore.apiClient.updateSARDetails(
      this.SARData.id,
      updates,
    );

    runInAction(() => {
      this.updateLocalStatus(this.statusForUpdate);
    });
  }

  /** Update victim impact statement */
  async updateVictimImpactStatement(value: string): Promise<void> {
    return this.updateStringField("victimImpactStatement", value);
  }

  /** Update defendant statement */
  async updateDefendantStatement(value: string): Promise<void> {
    return this.updateStringField("defendantStatement", value);
  }

  /** Update community strategy recommendation */
  async updateCommunityStrategyRecommendation(value: string): Promise<void> {
    return this.updateStringField("communityStrategyRecommendation", value);
  }

  /** Update institutional strategy recommendation */
  async updateInstitutionalStrategyRecommendation(
    value: string,
  ): Promise<void> {
    return this.updateStringField("institutionalStrategyRecommendation", value);
  }

  /** Update criminal history summary */
  async updateCriminalHistorySummary(value: string): Promise<void> {
    return this.updateStringField("criminalHistorySummary", value);
  }

  /** Update employment summary */
  async updateEmploymentSummary(value: string): Promise<void> {
    return this.updateStringField("employmentSummary", value);
  }

  /** Update family and social support summary */
  async updateFamilyAndSocialSupportSummary(value: string): Promise<void> {
    return this.updateStringField("familyAndSocialSupportSummary", value);
  }

  /** Update housing summary */
  async updateHousingSummary(value: string): Promise<void> {
    return this.updateStringField("housingSummary", value);
  }

  /** Update home plan */
  async updateHomePlan(value: string): Promise<void> {
    return this.updateStringField("homePlan", value);
  }

  /** Update peer associates summary */
  async updatePeerAssociatesSummary(value: string): Promise<void> {
    return this.updateStringField("peerAssociatesSummary", value);
  }

  /** Update criminal attitudes summary */
  async updateCriminalAttitudesSummary(value: string): Promise<void> {
    return this.updateStringField("criminalAttitudesSummary", value);
  }

  /** Update responsivity and barriers summary */
  async updateResponsivityAndBarriersSummary(value: string): Promise<void> {
    return this.updateStringField("responsivityAndBarriersSummary", value);
  }

  /** Update sexual history summary */
  async updateSexualHistorySummary(value: string): Promise<void> {
    return this.updateStringField("sexualHistorySummary", value);
  }

  /** Update drug history summary */
  async updateDrugHistorySummary(value: string): Promise<void> {
    return this.updateStringField("drugHistorySummary", value);
  }

  get orasData(): ORASFormData | null {
    const sarData = this.SARData;
    if (!sarData) return null;
    const data = pick(
      sarData,
      Object.keys(ORAS_EMPTY_FORM) as (keyof ORASFormData)[],
    );
    if (!sarData.assessmentDate && !data.assessmentAdministeredBy) {
      const staffName = this.sentencingStore.staffStore.staffInfo?.fullName;
      if (staffName)
        data.assessmentAdministeredBy = formatPersonName(staffName);
    }
    return data;
  }

  async saveORASData(data: ORASFormData): Promise<void> {
    const sarData = this.SARData;
    if (!sarData) return;

    type ScoredDomain = DomainConfig & {
      scoreField: keyof ORASFormData;
      riskLevelField: ORASDomainRiskLevelField;
      riskLevelCutoffs: Boundaries;
    };
    const derivedRiskLevels = Object.fromEntries(
      getDomainsForAssessmentType(data.assessmentType)
        .filter(
          (d): d is ScoredDomain =>
            !!(d.scoreField && d.riskLevelField && d.riskLevelCutoffs),
        )
        .map((d) => [
          d.riskLevelField,
          deriveDomainRiskLevel(
            getScoreWithAgePoint(
              d,
              data[d.scoreField] as number | null,
              data.ageLevel,
            ),
            d.riskLevelCutoffs,
          ),
        ]),
    );

    const payload = {
      ...data,
      ...derivedRiskLevels,
      // The import sets this from the source's assessment level; manually
      // entered scores get it from the score thresholds instead.
      assessmentRiskLevel:
        data.assessmentScore == null
          ? null
          : getAssessmentRiskLevel(
              data.assessmentType,
              data.assessmentScore,
              sarData.client?.gender,
            ),
      ORASLastUpdatedAt: new Date(),
      ORASEnteredManually: true,
    };

    const riskLevelChanged =
      payload.assessmentRiskLevel !== sarData.assessmentRiskLevel;

    runInAction(() => {
      Object.assign(sarData, payload);
      this.markFieldAsEditedLocally("offenderAssessment");
    });

    const updates: Partial<MutableSARAttributes> = {
      ...payload,
      status: this.statusForUpdate,
    };
    if (this.metadata) {
      updates.metadata = this.metadata as SARMetadata;
    }

    await this.sentencingStore.apiClient.updateSARDetails(sarData.id, updates);

    runInAction(() => {
      this.updateLocalStatus(this.statusForUpdate);
    });

    if (riskLevelChanged) this.loadInsight();
  }

  /** Update employed at offense */
  async updateEmployedAtOffense(value: boolean | null): Promise<void> {
    if (!this.SARData || this.isLocked) return;

    const sarId = this.SARData.id;

    runInAction(() => {
      if (this.SARData) {
        this.SARData.employedAtOffense = value;
      }
    });

    const updates: Partial<MutableSARAttributes> = {
      employedAtOffense: value,
      status: this.statusForUpdate,
    };

    // Only include metadata if it exists to avoid validation errors
    if (this.metadata) {
      updates.metadata = this.metadata as SARMetadata;
    }

    await this.sentencingStore.apiClient.updateSARDetails(sarId, updates);

    // Update local status after successful save
    runInAction(() => {
      this.updateLocalStatus(this.statusForUpdate);
    });
  }

  /** Update level of education */
  async updateLevelOfEducation(value: SAR["levelOfEducation"]): Promise<void> {
    if (!this.SARData || this.isLocked) return;

    runInAction(() => {
      if (this.SARData) {
        this.SARData.levelOfEducation = value;
      }
    });

    const updates: Partial<MutableSARAttributes> = {
      levelOfEducation: value ?? undefined,
      status: this.statusForUpdate,
    };

    await this.sentencingStore.apiClient.updateSARDetails(
      this.SARData.id,
      updates,
    );

    runInAction(() => {
      this.updateLocalStatus(this.statusForUpdate);
    });
  }

  /** Update father name */
  async updateFatherName(value: string): Promise<void> {
    if (!this.SARData || this.isLocked) return;

    runInAction(() => {
      if (this.SARData?.client) {
        this.SARData.client.fatherName = value;
      }
    });

    const updates: Partial<MutableSARAttributes> = {
      fatherName: value,
      status: this.statusForUpdate,
    };

    await this.sentencingStore.apiClient.updateSARDetails(
      this.SARData.id,
      updates,
    );

    runInAction(() => {
      this.updateLocalStatus(this.statusForUpdate);
    });
  }

  /** Update mother name */
  async updateMotherName(value: string): Promise<void> {
    if (!this.SARData || this.isLocked) return;

    runInAction(() => {
      if (this.SARData?.client) {
        this.SARData.client.motherName = value;
      }
    });

    const updates: Partial<MutableSARAttributes> = {
      motherName: value,
      status: this.statusForUpdate,
    };

    await this.sentencingStore.apiClient.updateSARDetails(
      this.SARData.id,
      updates,
    );

    runInAction(() => {
      this.updateLocalStatus(this.statusForUpdate);
    });
  }

  /** Update guardian name */
  async updateGuardianName(value: string): Promise<void> {
    if (!this.SARData || this.isLocked) return;

    runInAction(() => {
      if (this.SARData?.client) {
        this.SARData.client.guardianName = value;
      }
    });

    const updates: Partial<MutableSARAttributes> = {
      guardianName: value,
      status: this.statusForUpdate,
    };

    await this.sentencingStore.apiClient.updateSARDetails(
      this.SARData.id,
      updates,
    );

    runInAction(() => {
      this.updateLocalStatus(this.statusForUpdate);
    });
  }

  /**
   * Mark a field as edited in local state immediately (synchronous)
   * This updates the UI instantly when user is typing
   * Called by SkippableTextArea on every keystroke (before debounce)
   * Guards against duplicate work - returns early if already marked
   */
  markFieldAsEditedLocally(section: string): void {
    if (!this.SARData) return;

    const currentMetadata = this.metadata ?? {};
    const currentSections = currentMetadata.sections;

    // Only update if not already marked as edited (guard against duplicate work)
    const sectionData = currentSections?.[section as keyof SARMetadataSections];
    if (
      sectionData &&
      typeof sectionData === "object" &&
      "edited" in sectionData &&
      sectionData.edited
    ) {
      return; // Already marked as edited - no work needed
    }

    const updatedMetadata: SARMetadata = {
      ...currentMetadata,
      version: "1.0",
      sections: {
        keyConsiderations: currentSections?.keyConsiderations ?? {
          areasOfNeed: { skipped: false },
          mitigatingFactors: { skipped: false },
        },
        defendantStatement:
          section === "defendantStatement"
            ? {
                skipped: currentSections?.defendantStatement?.skipped ?? false,
                edited: true,
              }
            : currentSections?.defendantStatement ?? {
                skipped: false,
              },
        victimImpactStatement:
          section === "victimImpactStatement"
            ? {
                skipped:
                  currentSections?.victimImpactStatement?.skipped ?? false,
                edited: true,
              }
            : currentSections?.victimImpactStatement ?? {
                skipped: false,
              },
        recommendation:
          section === "recommendation"
            ? {
                skipped: currentSections?.recommendation?.skipped ?? false,
                edited: true,
              }
            : currentSections?.recommendation ?? {
                skipped: false,
              },
        priorTreatmentHistory:
          section === "priorTreatmentHistory"
            ? { edited: true }
            : currentSections?.priorTreatmentHistory,
        offenderAssessment:
          section === "offenderAssessment"
            ? { edited: true }
            : currentSections?.offenderAssessment,
      },
    };

    // Update local state immediately (synchronous for instant UI feedback)
    runInAction(() => {
      if (this.SARData) {
        this.SARData.metadata = updatedMetadata;
      }
    });
  }

  /** Update field skip state - generalized for any section/field (future-proof) */
  async updateFieldSkipped(
    section: string,
    skipped: boolean,
    subSection?: string | undefined,
  ): Promise<void> {
    if (!this.SARData || this.isLocked) return;

    // Update metadata with proper structure
    const currentMetadata = this.metadata ?? {};
    const currentSections = currentMetadata.sections;

    const updatedMetadata: SARMetadata = {
      ...currentMetadata,
      version: "1.0",
      sections: {
        // Preserve all existing sections to satisfy backend validation
        keyConsiderations: {
          areasOfNeed:
            subSection === "areasOfNeed"
              ? { skipped }
              : currentSections?.keyConsiderations?.areasOfNeed ?? {
                  skipped: false,
                },
          mitigatingFactors:
            subSection === "mitigatingFactors"
              ? { skipped }
              : currentSections?.keyConsiderations?.mitigatingFactors ?? {
                  skipped: false,
                },
        },
        defendantStatement:
          section === "defendantStatement"
            ? {
                skipped,
                edited: currentSections?.defendantStatement?.edited,
              }
            : currentSections?.defendantStatement ?? {
                skipped: false,
              },
        victimImpactStatement:
          section === "victimImpactStatement"
            ? {
                skipped,
                edited: currentSections?.victimImpactStatement?.edited,
              }
            : currentSections?.victimImpactStatement ?? {
                skipped: false,
              },
        recommendation:
          section === "recommendation"
            ? {
                skipped,
                edited: currentSections?.recommendation?.edited,
              }
            : currentSections?.recommendation ?? {
                skipped: false,
              },
        priorTreatmentHistory: currentSections?.priorTreatmentHistory,
        offenderAssessment: currentSections?.offenderAssessment,
      },
    };

    // Update local state immediately for instant UI feedback
    runInAction(() => {
      if (this.SARData) {
        this.SARData.metadata = updatedMetadata;
      }
    });

    // Persist to backend with updated status
    await this.sentencingStore.apiClient.updateSARDetails(this.SARData.id, {
      metadata: updatedMetadata,
      status: this.statusForUpdate,
    } as Partial<MutableSARAttributes>);

    // Update local status after successful save
    runInAction(() => {
      this.updateLocalStatus(this.statusForUpdate);
    });
  }

  // Computed properties for skip state
  get needsSkipped(): boolean {
    return (
      this.metadata?.["sections"]?.["keyConsiderations"]?.["areasOfNeed"]?.[
        "skipped"
      ] === true
    );
  }

  get factorsSkipped(): boolean {
    return (
      this.metadata?.["sections"]?.["keyConsiderations"]?.[
        "mitigatingFactors"
      ]?.["skipped"] === true
    );
  }

  /** Display labels for areas of need, replacing the "Other" placeholder with
   * the free-text value. Returns [] if the section was skipped. */
  get needsDisplayItems(): string[] {
    if (this.needsSkipped) return [];
    const items = mapEnumKeysToDisplay(
      NeedsToBeAddressed,
      this.SARData?.needsToBeAddressed,
    ).filter((item) => item !== OTHER_OPTION);
    if (this.SARData?.otherNeedToBeAddressed) {
      items.push(this.SARData.otherNeedToBeAddressed);
    }
    return items;
  }

  /** Display labels for mitigating factors, replacing the "Other" placeholder
   * with the free-text value. Returns [] if the section was skipped. */
  get factorsDisplayItems(): string[] {
    if (this.factorsSkipped) return [];
    const items = mapEnumKeysToDisplay(
      ProtectiveFactors,
      this.SARData?.mitigatingFactors,
    ).filter((item) => item !== OTHER_OPTION);
    if (this.SARData?.otherMitigatingFactor) {
      items.push(this.SARData.otherMitigatingFactor);
    }
    return items;
  }

  /**
   * Data for the Risk Profile Summary card in the SAR report.
   * Groups domains by risk level (HIGH/MODERATE/LOW) for display.
   * Returns null when assessmentType is missing, no ORAS was administered,
   * or the defendant declined to participate.
   */
  get riskProfileCardData(): RiskProfileCardData | null {
    const sarData = this.SARData;
    if (!sarData?.assessmentType || !this.hasOrasAssessment) return null;

    const domains = getDomainsForAssessmentType(sarData.assessmentType);
    const grouped: Record<RiskLevelKey, string[]> = {
      HIGH: [],
      MODERATE: [],
      LOW: [],
    };

    for (const domain of domains) {
      if (!domain.riskLevelField) continue;
      // Dynamic field access is necessary here: domain configs store risk-level
      // field names as strings, so keyof-narrowing is the best available approach.
      const level = sarData[
        domain.riskLevelField as keyof typeof sarData
      ] as RiskLevelKey | null;
      if (level && level in grouped) {
        grouped[level].push(domain.title);
      }
    }

    return {
      assessmentType: sarData.assessmentType as AssessmentTypeKey,
      administeredBy: this.assessmentAdministeredBy,
      assessmentDate: sarData.assessmentDate
        ? formatLongDate(sarData.assessmentDate)
        : null,
      ageAtAssessment: this.offenderAssessment.ageAtAssessment,
      groupedDomains: grouped,
    };
  }

  get victimImpactStatementSkipped(): boolean {
    return this.isSectionSkipped("victimImpactStatement");
  }

  get defendantStatementSkipped(): boolean {
    return this.isSectionSkipped("defendantStatement");
  }

  get recommendationSkipped(): boolean {
    return this.isSectionSkipped("recommendation");
  }

  /** Get section statuses for navigation indicators */
  get sectionStatuses(): Record<ProgressSection, SectionStatus> {
    // These sections are removed from the UI when the defendant declines, so
    // they should not block download readiness.
    const declined = this.defendantDeclinedToParticipate;
    return {
      [SARSection.CASE_INFORMATION]: this.getCaseInfoStatus(),
      [SARSection.KEY_CONSIDERATIONS]: declined
        ? "complete"
        : this.getKeyConsiderationsStatus(),
      [SARSection.DEFENDANTS_VERSION]: declined
        ? "complete"
        : this.getTextFieldStatus("defendantStatement"),
      [SARSection.VICTIM_IMPACT]: this.getTextFieldStatus(
        "victimImpactStatement",
      ),
      [SARSection.OFFENDER_ASSESSMENT]: this.getOffenderAssessmentStatus(),
      [SARSection.PRIOR_TREATMENT_HISTORY]:
        this.getPriorTreatmentHistoryStatus(),
      [SARSection.RECOMMENDATION]: declined
        ? "complete"
        : this.getRecommendationStatus(),
    };
  }

  /**
   * Whether every section this officer is responsible for is complete.
   */
  get isReadyForDownload(): boolean {
    const sections = this.SARSections;
    const statuses = this.sectionStatuses;
    return PROGRESS_SECTIONS.filter((section) =>
      sections.includes(section),
    ).every((section) => statuses[section] === "complete");
  }

  /**
   * Collects the offender assessment field values based on visible ORAS domains.
   * Shared by overallProgress and getOffenderAssessmentStatus so the field
   * lists are defined in one place.
   */
  private get offenderAssessmentFields(): {
    summaries: (string | null | undefined)[];
    formFields: (string | number | null | undefined)[];
  } {
    if (this.defendantDeclinedToParticipate) {
      return {
        summaries: [
          this.SARData?.defendantStatement,
          this.SARData?.criminalHistorySummary,
        ],
        formFields: [],
      };
    }

    const domains = getDomainsForAssessmentType(this.SARData?.assessmentType);

    const summaries = domains
      .map((d) => DOMAIN_TO_SUMMARY_FIELD[d.key])
      .filter(Boolean)
      .map((field) => this.SARData?.[field]);

    if (this.SARData?.involvesSexCrime) {
      summaries.push(this.SARData?.sexualHistorySummary);
    }

    const formFields: (string | number | null | undefined)[] = [];
    if (domains.some((d) => d.key === "educationEmployment")) {
      formFields.push(this.SARData?.levelOfEducation);
    }
    if (domains.some((d) => d.key === "familySocialSupport")) {
      formFields.push(
        this.SARData?.client?.fatherName,
        this.SARData?.client?.motherName,
        this.SARData?.client?.guardianName,
      );
    }
    if (this.SARData?.static99RCompleted) {
      formFields.push(this.SARData?.static99RScore);
    }

    return { summaries, formFields };
  }

  /** Private getter for metadata with proper typing */
  private get metadata(): Partial<SARMetadata> | null | undefined {
    return this.SARData?.metadata as Partial<SARMetadata> | null | undefined;
  }

  /** Helper: Check if a section/subsection is skipped */
  private isSectionSkipped(section: string, subSection?: string): boolean {
    const metadata = this.metadata;

    if (!metadata?.sections) return false;

    // Handle keyConsiderations specially since it has a nested structure
    if (section === "keyConsiderations" && subSection) {
      const keyConsiderations = metadata.sections.keyConsiderations;
      if (subSection === "areasOfNeed") {
        return keyConsiderations?.areasOfNeed?.skipped === true;
      }
      if (subSection === "mitigatingFactors") {
        return keyConsiderations?.mitigatingFactors?.skipped === true;
      }
      return false;
    }

    // For top-level sections (defendantStatement, victimImpactStatement, recommendation)
    const sectionData = metadata.sections[section as keyof SARMetadataSections];
    if (
      sectionData &&
      typeof sectionData === "object" &&
      "skipped" in sectionData
    ) {
      return sectionData.skipped === true;
    }

    return false;
  }

  /** Helper: Get status for a simple text field section */
  private getTextFieldStatus(
    fieldName: "victimImpactStatement" | "defendantStatement",
  ): SectionStatus {
    if (
      this.isPSRAllExceptVictimImpact &&
      fieldName === "victimImpactStatement"
    )
      return "complete";

    const fieldValue = this.SARData?.[fieldName];
    const hasContent = fieldValue ? fieldValue.trim() !== "" : false;
    const isSkipped = this.isSectionSkipped(fieldName);
    const isEdited =
      this.metadata?.["sections"]?.[fieldName]?.["edited"] === true;

    // If has content OR is skipped, complete
    if (hasContent || isSkipped) {
      return "complete";
    }

    // If empty but has been edited (user visited and left empty), show incomplete
    if (!hasContent && !isSkipped && isEdited) {
      return "incomplete";
    }

    // If never visited (empty, not skipped, not edited), show nothing
    return "empty";
  }

  private getPriorTreatmentHistoryStatus(): SectionStatus {
    if (this.defendantDeclinedToParticipate) return "complete";
    const summary = this.SARData?.priorTreatmentHistorySummary;
    const hasContent = !!summary && summary.trim() !== "";
    const isEdited =
      this.metadata?.sections?.priorTreatmentHistory?.edited === true;

    if (hasContent) return "complete";
    if (isEdited) return "incomplete";
    return "empty";
  }

  /** Get Case Information section status */
  private getCaseInfoStatus(): SectionStatus {
    if (this.charges.length === 0) return "empty";

    const hasJudgeName = !!this.SARData?.requestingJudgeName;
    let hasAnyValue = hasJudgeName;
    let allFieldsComplete = hasJudgeName;

    this.charges.forEach((charge) => {
      REQUIRED_FIELD_IDS.forEach((fieldId) => {
        const value = charge[fieldId as keyof typeof charge];
        if (value !== null && value !== undefined && value !== "") {
          hasAnyValue = true;
        } else {
          allFieldsComplete = false;
        }
      });
    });

    if (!hasAnyValue) return "empty";
    if (allFieldsComplete) return "complete";
    return "incomplete";
  }

  /** Get Key Considerations section status */
  private getKeyConsiderationsStatus(): SectionStatus {
    const needsValues = this.SARData?.needsToBeAddressed;
    const mitigatingValues = this.SARData?.mitigatingFactors;

    const hasNeeds = needsValues && needsValues.length > 0;
    const hasFactors = mitigatingValues && mitigatingValues.length > 0;

    const needsSkipped = this.isSectionSkipped(
      "keyConsiderations",
      "areasOfNeed",
    );
    const factorsSkipped = this.isSectionSkipped(
      "keyConsiderations",
      "mitigatingFactors",
    );

    // If both fields are empty and not skipped, show nothing (empty)
    if (!hasNeeds && !hasFactors && !needsSkipped && !factorsSkipped) {
      return "empty";
    }

    // If both fields are filled OR both are skipped, complete (no icon)
    const needsComplete = hasNeeds || needsSkipped;
    const factorsComplete = hasFactors || factorsSkipped;

    if (needsComplete && factorsComplete) {
      return "complete";
    }

    // If only one field is filled/skipped, incomplete (show warning)
    return "incomplete";
  }

  /** Get Recommendation section status */
  private getRecommendationStatus(): SectionStatus {
    const hasCommunity =
      this.SARData?.communityStrategyRecommendation &&
      this.SARData.communityStrategyRecommendation.trim() !== "";
    const hasHomePlan =
      this.SARData?.homePlan && this.SARData.homePlan.trim() !== "";
    const hasInstitutional =
      this.SARData?.institutionalStrategyRecommendation &&
      this.SARData.institutionalStrategyRecommendation.trim() !== "";

    const isSkipped = this.isSectionSkipped("recommendation");
    const isEdited =
      this.metadata?.["sections"]?.["recommendation"]?.["edited"] === true;

    // If all fields are filled OR is skipped, complete
    if ((hasCommunity && hasHomePlan && hasInstitutional) || isSkipped) {
      return "complete";
    }

    // If all fields are empty and not skipped but has been edited, show incomplete
    if (
      !hasCommunity &&
      !hasHomePlan &&
      !hasInstitutional &&
      !isSkipped &&
      isEdited
    ) {
      return "incomplete";
    }

    // If all fields are empty and never visited, show nothing
    if (
      !hasCommunity &&
      !hasHomePlan &&
      !hasInstitutional &&
      !isSkipped &&
      !isEdited
    ) {
      return "empty";
    }

    // If any field is missing, incomplete (show warning)
    return "incomplete";
  }

  /** Get Offender Assessment section status */
  private getOffenderAssessmentStatus(): SectionStatus {
    const { summaries, formFields } = this.offenderAssessmentFields;

    const summaryFilledCount = summaries.filter(
      (s) => s && s.trim() !== "",
    ).length;
    const formFilledCount = formFields.filter(
      (f) => f !== null && f !== undefined && f.toString().trim() !== "",
    ).length;

    const totalFields = summaries.length + formFields.length;
    const totalFilledCount = summaryFilledCount + formFilledCount;

    // Check if only the criminal history default is filled (nothing else touched)
    const criminalHistoryValue = this.SARData?.criminalHistorySummary?.trim();
    const onlyDefaultCriminalHistory =
      criminalHistoryValue === CRIMINAL_HISTORY_DEFAULT &&
      totalFilledCount === 1 &&
      formFilledCount === 0;

    const isEdited =
      this.metadata?.sections?.offenderAssessment?.edited === true;

    if (!this.SARData?.assessmentDate && !this.defendantDeclinedToParticipate)
      return "incomplete";
    if (totalFilledCount === totalFields) return "complete";
    if (totalFilledCount > 0 && !onlyDefaultCriminalHistory)
      return "incomplete";
    if (isEdited) return "incomplete";
    return "empty";
  }
}
